/**
 * ThresholdRuleService - Handles threshold-based rule processing
 *
 * Threshold rules trigger escalating consequences when a student accumulates
 * a certain number of consequences (demerits/detentions) within a timeframe.
 */

import { phaseRuleAllowsRecord, isPhaseExecutionError } from '../../domain/phaseRuntime';
import { ruleRecordInRange } from '../../domain/schoolRulePeriods.js';
import { isSchoolRulePeriodError } from '../utils/dateHelpers';

import {
  collection,
  query,
  where,
  getDocs,
  Timestamp
} from 'firebase/firestore';
import { db } from '../../../firebase';
import { getDateRangeForRuleWithDetection } from '../utils/ruleDateHelpers';
import { disciplineLogger } from '../../../utils/disciplineLogger';
import { consequenceTypesService } from '../../consequenceTypesService';
import { eventStyleConfig } from '../config/eventStyleConfig';
import { processingGuard } from '../utils/ProcessingGuard';
import { snapshotStaffIdentity } from '../utils/staffIdentity';

import { completeBehaviourThresholdCheck } from '../recordBehaviourAction';
import { isThresholdCountable } from '../utils/thresholdEligibility';
export { isThresholdCountable };

export const resolveConfiguredConsequenceState = (itemType, consequenceType) => {
  if (!consequenceType) {
    throw new Error(`Consequence type "${itemType}" is not configured for this school`);
  }
  const requiresAction = consequenceType.requiresAction === true;
  return { requiresAction, status: requiresAction ? 'pending' : 'archived' };
};

export class ThresholdRuleService {
  constructor(dependencies) {
    this.ruleService = dependencies.ruleService;
    this.usesTrustedThresholds = !this.ruleService?.collectionName
      || this.ruleService.collectionName === 'disciplineRules';
    this.consequenceService = dependencies.consequenceService;
    this.ruleEvaluator = dependencies.ruleEvaluator; // For escalation checks
    this.typeNormalizer = dependencies.typeNormalizer;
  }

  /**
   * Check and process threshold rules after a consequence is created
   * @param {Object} consequence - The created consequence
   * @param {string} studentId - Student ID
   * @param {Object} studentData - Pre-fetched student data
   * @returns {Promise<void>}
   */
  async checkThresholdsAfterCreation(consequence, studentId, studentData) {
    try {
      // CRITICAL: Extract schoolId from consequence
      const schoolId = consequence.schoolId;
      if (!schoolId) {
        disciplineLogger.critical('❌ Cannot check thresholds: consequence.schoolId is missing', {
          consequenceId: consequence.id,
          studentId
        });
        return;
      }

      disciplineLogger.info(`⚙️ CHECKING THRESHOLDS AFTER CREATION`, {
        consequenceId: consequence.id,
        consequenceType: consequence.type,
        studentId,
        schoolId,
        triggeredBy: consequence.ruleName || consequence.triggeredByRule || 'manual'
      }, 'thresholds');

      const followup = this.usesTrustedThresholds ? consequence.thresholdFollowup : null;
      if (followup?.status === 'complete') return;
      const thresholdRules = followup?.rules || await this.getThresholdRules(schoolId);
      if (followup) {
        const records = new Map(studentData.consequences.map(record => [record.id, record]));
        if (!records.has(consequence.id)) records.set(consequence.id, consequence);
        studentData = {...studentData, consequences: followup.candidateIds.map(id => records.get(id)).filter(Boolean)};
      }
      const phasePolicy = await this.ruleService.getPhasePolicy?.(schoolId);

      disciplineLogger.info(`Found ${thresholdRules.length} active threshold rules`, {
        rules: thresholdRules.map(r => ({
          title: r.title,
          countType: r.countType || r.monitorType,
          threshold: r.count || r.threshold,
          consequenceType: r.consequenceType || r.escalateType
        }))
      });

      if (thresholdRules.length === 0) {
        disciplineLogger.debug('No threshold rules to check');
        return;
      }

      let triggeredCount = 0;
      for (const rule of thresholdRules) {
        if (followup && Object.prototype.hasOwnProperty.call(
            followup.results || {}, rule.id,
        )) continue;
        const triggered = await this.evaluateThresholdRule(rule, consequence, studentId, studentData, phasePolicy);
        if (followup && !triggered) await completeBehaviourThresholdCheck(consequence, rule.id);
        if (triggered) {
          triggeredCount++;
          disciplineLogger.critical(`✓ Rule triggered: "${rule.title}"`, {
            triggeredRule: rule.title
          }, 'thresholds');
          // Continue checking other rules - multiple rules can trigger
        }
      }

      disciplineLogger.info(`✓ Threshold check complete`, {
        triggeredCount,
        totalRules: thresholdRules.length
      }, 'thresholds');
    } catch (error) {
      if (isSchoolRulePeriodError(error) || isPhaseExecutionError(error)) throw error;
      if (this.usesTrustedThresholds) {
        error.thresholdProcessingFailed = true;
        throw error;
      }
      console.error('Error checking thresholds after creation:', error);
    }
  }

  /**
   * Get active threshold rules
   * @param {string} schoolId - School ID (required for hierarchical collections)
   * @returns {Promise<Array>} Threshold rules
   */
  async getThresholdRules(schoolId) {
    if (!schoolId) {
      throw new Error('schoolId is required for threshold rules query');
    }

    try {
      const activeRules = await this.ruleService.getActiveRules(schoolId);
      const thresholdRules = activeRules.filter(r => r.type === 'threshold');
      return thresholdRules.sort((a, b) => (b.priority || 0) - (a.priority || 0));
    } catch (error) {
      if (isSchoolRulePeriodError(error) || isPhaseExecutionError(error)) throw error;
      if (this.usesTrustedThresholds) throw error;
      console.error('Error fetching threshold rules:', error);
      return [];
    }
  }

  /**
   * Evaluate a single threshold rule
   * @param {Object} rule - The rule to evaluate
   * @param {Object} consequence - The triggering consequence
   * @param {string} studentId - Student ID
   * @param {Object} studentData - Pre-fetched student data
   * @param {Object} phasePolicy - Saved Domain applicability policy
   * @returns {Promise<boolean>} True if rule was triggered
   */
  async evaluateThresholdRule(rule, consequence, studentId, studentData, phasePolicy) {
    phasePolicy ??= await this.ruleService.getPhasePolicy?.(consequence.schoolId);
    if (!phaseRuleAllowsRecord(phasePolicy, rule, consequence)) return false;
    // Map fields from DB format
    const threshold = rule.count || rule.threshold || 0;

    // Support both single countType and array countTypes
    const monitorTypes = rule.countTypes && Array.isArray(rule.countTypes)
      ? rule.countTypes
      : [rule.countType || rule.monitorType || 'demerit'];

    const timeframe = rule.timeframe || 'weekly';
    const consequenceType = rule.consequenceType || rule.escalateType || 'detention';

    // Extract schoolId early for use in history filter
    const schoolId = consequence.schoolId || rule.schoolId || studentData.schoolId;

    disciplineLogger.info(`🔍 THRESHOLD CHECK: "${rule.title}"`, {
      ruleId: rule.id,
      countType: rule.countType,
      countTypes: rule.countTypes,
      monitorType: rule.monitorType,
      threshold,
      monitorTypes_resolved: monitorTypes,
      consequenceType,
      timeframe,
      hasHistoryFilter: !!rule.historyFilter
    }, 'thresholds');

    const dateRange = await getDateRangeForRuleWithDetection(rule, timeframe, rule.timeCount || 1, schoolId, {
      referenceDate: consequence.issuedAt || consequence.createdAt,
    });
    studentData = {...studentData, consequences: studentData.consequences.filter(
      candidate => ruleRecordInRange(candidate, dateRange) &&
        phaseRuleAllowsRecord(phasePolicy, rule, candidate, {historical: true}),
    )};

    // Check history filter (checks consequence history, not mention history)
    if (rule.historyFilter) {
      disciplineLogger.debug('Rule has history filter', {
        filter: rule.historyFilter,
        studentId,
        consequenceType
      });
      const passesHistoryFilter = await this.checkHistoryFilter(rule.historyFilter, studentId, consequenceType, studentData, schoolId, dateRange);
      if (!passesHistoryFilter) {
        disciplineLogger.debug('Rule failed history filter check');
        return false;
      }
      disciplineLogger.debug('Rule passed history filter check');
    }


    disciplineLogger.debug(`📊 Using pre-fetched data`, {
      monitorTypes,
      dateRangeStart: dateRange.start.toISOString(),
      dateRangeEnd: dateRange.end.toISOString(),
      totalConsequences: studentData.consequences.length
    }, 'thresholds');

    // Filter consequences from pre-fetched data instead of querying
    let matchingConsequences = [];

    for (const monitorType of monitorTypes) {
      // Use TypeNormalizer for consistency
      const typeId = await this.typeNormalizer.normalizeToId(monitorType, schoolId);
      const normalizedType = await this.typeNormalizer.normalize(monitorType, schoolId);

      disciplineLogger.debug(`Filtering for type: ${monitorType} → ${normalizedType} (ID: ${typeId})`, {
        original: monitorType,
        normalized: normalizedType,
        typeId: typeId
      }, 'thresholds');

      // Filter from pre-fetched data
      const filtered = studentData.consequences.filter(c => {
        // Check if consequence matches the type
        const typeMatch = (typeId && c.typeId === typeId) ||
                         c.type === normalizedType ||
                         c.type === monitorType ||
                         c.consequenceType === normalizedType ||
                         c.consequenceType === monitorType;

        // Soft-voided records remain auditable but cannot trigger a rule.
        const countable = isThresholdCountable(c);

        // Check date range
        const inDateRange = ruleRecordInRange(c, dateRange);

        return typeMatch && countable && inDateRange;
      });

      matchingConsequences.push(...filtered);
    }

    // Convert to doc-like format for compatibility
    const uniqueConsequences = [...new Map(matchingConsequences.map(c => [c.id, c])).values()];
    const allDocs = uniqueConsequences.map(c => ({
      id: c.id,
      data: () => c
    }));

    // Create a snapshot-like object for compatibility with rest of code
    const snapshot = { docs: allDocs };

    // Log ALL found consequences/mentions before filtering
    const allFound = snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        type: data.type || data.mentionType || 'mention',
        consumed: !!data.usedByThresholdRule || !!data.actioned,
        consumedBy: data.usedByThresholdRule,
        issuedAt: data.issuedAt?.toDate?.()
      };
    });

    disciplineLogger.debug(`📋 Found ${allFound.length} items matching query`, {
      items: allFound
    });

    // Filter out consequences already used by a threshold or escalation rule
    const unusedDocs = snapshot.docs.filter(doc => {
      const data = doc.data();
      return !data.usedByThresholdRule && !data.usedByEscalationRule;
    });

    const count = unusedDocs.length;
    disciplineLogger.info(`✓ Evaluating rule: ${count}/${threshold} ${monitorTypes.join('/')} (unused)`, {
      totalFound: allFound.length,
      unusedCount: count,
      consumedCount: allFound.length - count,
      monitoringTypes: monitorTypes
    }, 'thresholds');

    if (count >= threshold) {
      disciplineLogger.critical(`🚨 THRESHOLD MET: "${rule.title}"`, {
        threshold,
        actual: count,
        consequence: consequenceType,
        willCreate: true
      }, 'thresholds');
      await this.createThresholdConsequence(rule, consequence, unusedDocs, threshold);
      return true;
    }

    disciplineLogger.debug(`Threshold not met: ${count} < ${threshold}`);
    return false;
  }

  /**
   * Check if student passes history filter for consequence type
   * This checks CONSEQUENCE HISTORY, not mention history
   * @param {string} historyFilter - Filter type (first-offense, 2-plus, 5-plus)
   * @param {string} studentId - Student ID
   * @param {string} consequenceType - Type of consequence to check history for
   * @param {Object} studentData - Pre-fetched student data (optional for backward compatibility)
   * @param {string} schoolId - School ID for type normalization (optional for backward compatibility)
   * @returns {Promise<boolean>} True if passes filter
   */
  async checkHistoryFilter(historyFilter, studentId, consequenceType, studentData = null, schoolId = null, dateRange = null) {
    // Debug log incoming parameters
    disciplineLogger.info('checkHistoryFilter called with:', {
      historyFilter,
      studentId,
      consequenceType,
      hasStudentId: !!studentId,
      hasConsequenceType: !!consequenceType
    });

    // If no filter or 'all', pass immediately
    if (!historyFilter || historyFilter === 'all') {
      return true;
    }

    // Validate parameters
    if (!studentId) {
      disciplineLogger.critical('❌ checkHistoryFilter: studentId is undefined');
      return true; // Allow rule to pass if we can't check
    }

    // Validate consequenceType
    if (!consequenceType) {
      console.warn('⚠️ consequenceType is undefined/null, defaulting to "detention"');
      consequenceType = 'detention';
    }

    // Use TypeNormalizer for consistent type matching
    let normalizedConsequenceType;
    if (schoolId) {
      normalizedConsequenceType = await this.typeNormalizer.normalize(consequenceType, schoolId);
    } else {
      disciplineLogger.warn('⚠️ schoolId not provided to checkHistoryFilter, using original consequence type');
      normalizedConsequenceType = consequenceType;
    }

    // Handle case where normalization returns undefined
    if (!normalizedConsequenceType) {
      disciplineLogger.warn(`⚠️ Could not normalize consequence type: "${consequenceType}", using original`, {
        original: consequenceType
      });
      // Fall back to original type
      normalizedConsequenceType = consequenceType || 'detention';
    }

    disciplineLogger.info(`🔍 History filter check: "${historyFilter}"`, {
      consequenceType,
      normalizedConsequenceType,
      studentId
    });

    // Validate required fields before querying
    if (!studentId) {
      disciplineLogger.critical('❌ studentId is undefined in checkHistoryFilter');
      return true; // Allow rule to pass if we can't check history
    }

    if (!normalizedConsequenceType) {
      disciplineLogger.critical('❌ normalizedConsequenceType is still undefined after fallback');
      return true; // Allow rule to pass if we can't check history
    }

    // Use pre-fetched data if available, otherwise query
    let consequenceHistory;

    if (studentData) {
      // Filter from pre-fetched data
      const matchingConsequences = studentData.consequences.filter(c =>
        c.type === normalizedConsequenceType &&
        !c.undone
      );

      // Convert to doc-like format for compatibility
      consequenceHistory = matchingConsequences.map(c => ({
        id: c.id,
        data: () => c
      }));

      disciplineLogger.debug('Using pre-fetched data for history filter', {
        totalConsequences: studentData.consequences.length,
        matchingConsequences: consequenceHistory.length
      });
    } else {
      // Fallback to query (for backward compatibility)
      disciplineLogger.warn('No pre-fetched data, falling back to query');

      // CRITICAL: We need schoolId for hierarchical query but don't have it here
      // This fallback path should never execute if studentData is properly passed
      // Log critical error but try to continue
      disciplineLogger.critical('❌ Cannot query consequences: studentData not provided and schoolId unavailable', {
        studentId,
        consequenceType: normalizedConsequenceType,
        historyFilter
      });

      // Return empty to allow rule to pass (safe fallback)
      consequenceHistory = [];
    }

    if (dateRange) {
      consequenceHistory = consequenceHistory.filter(doc => ruleRecordInRange(doc.data(), dateRange));
    }

    // Log details of what was found
    const historyDetails = consequenceHistory.map(doc => ({
      id: doc.id,
      type: doc.data().type,
      consumed: !!doc.data().usedByThresholdRule,
      issuedAt: doc.data().issuedAt?.toDate?.()
    }));

    disciplineLogger.info(`📊 History found: ${consequenceHistory.length} ${consequenceType}`, {
      historyCount: consequenceHistory.length,
      includesConsumed: historyDetails.filter(h => h.consumed).length,
      details: historyDetails
    });

    // first-offense: never received this consequence type before
    if (historyFilter === 'first-offense') {
      const passes = consequenceHistory.length === 0;
      disciplineLogger.info(`${passes ? '✓' : '✗'} First-offense check`, {
        passes,
        reason: passes ? 'No prior consequences' : `Has ${consequenceHistory.length} prior`
      });
      return passes;
    }

    // 2-plus: received this consequence at least once before
    if (historyFilter === '2-plus') {
      const passes = consequenceHistory.length >= 1;
      disciplineLogger.info(`${passes ? '✓' : '✗'} 2-plus check`, {
        passes,
        count: consequenceHistory.length,
        required: 1
      });
      return passes;
    }

    // 5-plus: received this consequence at least 4 times before
    if (historyFilter === '5-plus') {
      const passes = consequenceHistory.length >= 4;
      disciplineLogger.info(`${passes ? '✓' : '✗'} 5-plus check`, {
        passes,
        count: consequenceHistory.length,
        required: 4
      });
      return passes;
    }

    return true;
  }

  /**
   * Check if threshold rule already triggered in this period
   * @param {string} ruleId - Rule ID
   * @param {string} studentId - Student ID
   * @param {Object} dateRange - Date range to check
   * @returns {Promise<boolean>} True if already triggered
   */
  async checkAlreadyTriggered(ruleId, studentId, dateRange, schoolId) {
    disciplineLogger.debug(`🔍 Checking if rule already triggered in period`, {
      ruleId,
      studentId,
      periodStart: dateRange.start.toISOString(),
      periodEnd: dateRange.end.toISOString()
    });

    // CRITICAL: Validate schoolId for hierarchical query
    if (!schoolId) {
      disciplineLogger.critical('❌ Cannot check already triggered: schoolId is missing', {
        ruleId,
        studentId
      });
      return false; // Assume not triggered to allow rule to proceed (safe fallback)
    }

    const existingQuery = query(
      collection(db, 'schools', schoolId, 'disciplineQueue'),
      where('studentId', '==', studentId),
      where('triggeredByRule', '==', ruleId),
      where('issuedAt', '>=', Timestamp.fromDate(dateRange.start))
    );

    const existing = await getDocs(existingQuery);
    processingGuard.trackReads('ThresholdService.checkAlreadyTriggered', existing.size, {
      ruleId,
      studentId
    });

    const alreadyTriggered = !existing.empty;

    if (alreadyTriggered) {
      const existingDocs = existing.docs.map(doc => ({
        id: doc.id,
        type: doc.data().type,
        issuedAt: doc.data().issuedAt?.toDate?.(),
        undone: doc.data().undone
      }));

      disciplineLogger.info(`✓ Found ${existing.docs.length} existing consequences from this rule`, {
        consequences: existingDocs
      });
    } else {
      disciplineLogger.debug(`No existing consequences from this rule in period`);
    }

    return alreadyTriggered;
  }

  // OLD ESCALATION METHODS REMOVED
  // Escalation is now handled by EscalationRuleService via RuleEvaluator
  // See ConsequenceService.createDisciplineConsequence() for the new flow

  /**
   * Create a threshold-triggered consequence
   * @param {Object} rule - The threshold rule
   * @param {Object} triggeringConsequence - The consequence that triggered threshold
   * @param {Array} unusedDocs - Unused consequence documents
   * @param {number} threshold - Threshold count
   * @returns {Promise<void>}
   */
  async createThresholdConsequence(rule, triggeringConsequence, unusedDocs, threshold) {
    // Only use the first 'threshold' number of unused consequences
    const consumedDocs = unusedDocs.slice(0, threshold);

    // Behaviour saves the counted records and response in one transaction.
    const monitorTypes = rule.countTypes && Array.isArray(rule.countTypes)
      ? rule.countTypes
      : [rule.countType || rule.monitorType || 'demerit'];

    const studentId = triggeringConsequence.studentId;
    let consequenceTypeToIssue = rule.consequenceType || rule.escalateType || 'detention';

    disciplineLogger.critical(`🎯 CREATING THRESHOLD CONSEQUENCE`, {
      ruleName: rule.title,
      ruleId: rule.id,
      triggeredByConsequence: {
        id: triggeringConsequence.id,
        type: triggeringConsequence.type
      },
      consumingDocs: consumedDocs.map(d => ({
        id: d.id,
        type: d.data().type,
        issuedAt: d.data().issuedAt?.toDate?.()
      })),
      initialConsequenceType: consequenceTypeToIssue,
      hasRuleEvaluator: !!this.ruleEvaluator
    });

    // Check if this consequence should be escalated via RuleEvaluator
    disciplineLogger.critical(`🔄 CHECKING ESCALATION`, {
      consequenceType: consequenceTypeToIssue,
      studentId,
      hasRuleEvaluator: !!this.ruleEvaluator,
      ruleEvaluatorType: this.ruleEvaluator ? typeof this.ruleEvaluator : 'undefined'
    }, 'escalations');

    const escalationResult = this.ruleEvaluator
      ? await this.ruleEvaluator.checkEscalationBeforeCreation(
          studentId,
          consequenceTypeToIssue,
          {
            schoolId: triggeringConsequence.schoolId, // CRITICAL: Pass schoolId for hierarchical collections
            ruleName: rule.title,
            ruleId: rule.id,
            referenceDate: triggeringConsequence.issuedAt || triggeringConsequence.createdAt,
            sourceRecord: triggeringConsequence,
            candidateIds: triggeringConsequence.thresholdFollowup?.candidateIds,
            triggeredByThreshold: true
          }
        )
      : null;

    disciplineLogger.critical(`🔄 ESCALATION CHECK RESULT`, {
      hasResult: !!escalationResult,
      result: escalationResult
    }, 'escalations');

    // Track if escalation happened and what to consume
    let escalationOccurred = false;
    let escalationDocsToConsume = [];
    let escalationRuleId = null;

    if (escalationResult) {
      consequenceTypeToIssue = escalationResult.escalatedType;
      escalationOccurred = true;
      escalationDocsToConsume = escalationResult.consumedDocs;
      escalationRuleId = escalationResult.escalationRuleId;

      disciplineLogger.critical(`⬆️ ESCALATION TRIGGERED`, {
        from: rule.consequenceType || rule.escalateType,
        to: consequenceTypeToIssue,
        escalationRuleName: escalationResult.escalationRuleTitle,
        consumingAdditional: escalationDocsToConsume.length
      }, 'escalations');
    } else {
      disciplineLogger.info(`No escalation needed for ${consequenceTypeToIssue}`);
    }

    // Build the threshold consequence with (possibly escalated) type
    const thresholdConsequence = await this.buildThresholdConsequence(
      rule,
      triggeringConsequence,
      consumedDocs,
      consequenceTypeToIssue,
      escalationResult, // Pass escalation info for message generation
      triggeringConsequence.schoolId // Pass schoolId for consequence type lookups
    );

    // Resolve period-dependent escalation and build successfully before consuming sources.
    // Try to mark documents as consumed BEFORE creating the consequence
    if (!this.usesTrustedThresholds && consumedDocs.length > 0) {
      try {
        disciplineLogger.info(`PRE-CONSUMING ${consumedDocs.length} documents before consequence creation`);
        await this.markConsequencesAsConsumed(consumedDocs, rule.id);
        disciplineLogger.critical(`PRE-CONSUMED ${consumedDocs.length} ${monitorTypes.join('/')}`, {
          ruleId: rule.id,
          phase: 'pre-creation'
        }, 'thresholds');
      } catch (error) {
        if (isSchoolRulePeriodError(error) || isPhaseExecutionError(error)) throw error;
        disciplineLogger.critical(`Failed to pre-consume documents, aborting consequence creation`, {
          ruleId: rule.id,
          error: error.message,
          documentsAffected: consumedDocs.map(d => d.id)
        });
        // ABORT: Don't create consequence if we can't mark triggers as consumed
        throw new Error(`Cannot create threshold consequence: Failed to mark triggering documents as consumed - ${error.message}`);
      }
    }

    // Create the consequence (with skipThresholds and skipEscalation to avoid recursion)
    // We already checked escalation above, so no need to check again
    const savedResponse = await this.consequenceService.createDisciplineConsequence(
      thresholdConsequence,
      { skipThresholds: true, skipEscalation: true, escalationResult,
        ...(escalationResult?.atomicPersistence ? { creationType: rule.consequenceType || rule.escalateType || 'detention' } : {}),
        phaseSource: { collection: 'disciplineQueue', id: triggeringConsequence.id },
        ...(this.usesTrustedThresholds ? { threshold: { ruleId: rule.id,
          expectedRuleRevision: rule.configurationRevision ?? 0 } } : {}) }
    );

    // Reflect only a successful joint save in the shared evaluation cache.
    if (this.usesTrustedThresholds) {
      for (const consumed of consumedDocs) {
        const data = consumed.data();
        data.usedByThresholdRule = rule.id;
        data.actioned = true;
        data.actionedBy = [...new Set([...(data.actionedBy || []), rule.id])];
      }
    }

    // If escalation occurred, also consume the detentions that triggered it
    if (escalationOccurred && !savedResponse?.escalationPersistence && escalationDocsToConsume.length > 0) {
      // Handle both single escalation (string) and cascaded escalations (array)
      const ruleIdForConsuming = Array.isArray(escalationRuleId)
        ? escalationRuleId[escalationRuleId.length - 1]  // Use last rule ID from cascade
        : escalationRuleId;

      try {
        await this.markConsequencesAsConsumed(escalationDocsToConsume, ruleIdForConsuming);
        disciplineLogger.critical(`CONSUMED ${escalationDocsToConsume.length} ${rule.consequenceType}`, {
          ruleId: ruleIdForConsuming,
          allRuleIds: escalationRuleId,
          reason: 'escalation'
        });
      } catch (error) {
        if (isSchoolRulePeriodError(error) || isPhaseExecutionError(error)) throw error;
        // Log the error but don't fail the entire operation
        // The consequence was already created, we just failed to mark escalation docs
        disciplineLogger.critical(`Failed to mark escalation documents as consumed`, {
          ruleId: ruleIdForConsuming,
          error: error.message,
          documentsAffected: escalationDocsToConsume.map(d => d.id),
          consequenceAlreadyCreated: true
        });
      }
    }
  }

  /**
   * Get the earliest timestamp from triggering consequences
   * This ensures threshold consequences appear inline with their triggers in the timeline
   * @param {Array} consequences - Array of triggering consequences
   * @returns {Timestamp} Earliest consequence timestamp
   */
  getEarliestConsequenceTimestamp(consequences) {
    if (!consequences || consequences.length === 0) {
      return Timestamp.now();
    }

    // Find the earliest consequence by timestamp
    const earliest = consequences.reduce((oldest, c) => {
      const cTime = c.issuedAt || c.createdAt;
      const oTime = oldest.issuedAt || oldest.createdAt;

      // Handle Firestore Timestamp objects
      const cSeconds = cTime?.seconds || cTime?.getTime?.() / 1000 || 0;
      const oSeconds = oTime?.seconds || oTime?.getTime?.() / 1000 || 0;

      return (cSeconds < oSeconds) ? c : oldest;
    });

    return earliest.issuedAt || earliest.createdAt || Timestamp.now();
  }

  /**
   * Build threshold consequence object
   * @param {Object} rule - The threshold rule
   * @param {Object} triggeringConsequence - The triggering consequence
   * @param {Array} triggeringDocs - Documents that triggered the threshold
   * @param {string} consequenceTypeOverride - Optional override for consequence type (used for escalation)
   * @param {Object} escalationInfo - Optional escalation information
   * @param {string} schoolId - School ID for consequence type lookups
   * @returns {Promise<Object>} Consequence data
   */
  async buildThresholdConsequence(rule, triggeringConsequence, triggeringDocs, consequenceTypeOverride = null, escalationInfo = null, schoolId = null) {
    // CRITICAL: Validate and ensure schoolId is available
    if (!schoolId) {
      schoolId = triggeringConsequence?.schoolId || rule?.schoolId;
      if (!schoolId) {
        disciplineLogger.critical('❌ Cannot build threshold consequence: missing schoolId', {
          ruleId: rule?.id,
          consequenceId: triggeringConsequence?.id
        });
        throw new Error('schoolId is required for buildThresholdConsequence');
      }
    }

    const itemType = consequenceTypeOverride || rule.consequenceType || rule.escalateType || 'detention';
    const consequenceTypeConfig = await consequenceTypesService.getByValue(itemType, schoolId);
    const { requiresAction, status } = resolveConfiguredConsequenceState(
      itemType,
      consequenceTypeConfig
    );

    const threshold = rule.count || rule.threshold || 0;
    const monitorTypes = rule.countTypes && Array.isArray(rule.countTypes)
      ? rule.countTypes
      : [rule.countType || rule.monitorType || 'demerit'];

    // Get proper labels for monitor types
    const monitorTypeLabels = await Promise.all(
      monitorTypes.map(async (type) => {
        const typeDoc = await consequenceTypesService.getByValue(type, schoolId);
        return typeDoc?.label || type;
      })
    );
    const monitorTypeLabel = monitorTypeLabels.join('/');

    const timeframe = rule.timeframe || 'weekly';
    const consequenceType = itemType;
    const consequenceCount = rule.consequenceCount || 1;
    const studentFirstName = triggeringConsequence.studentName?.split(' ')[0] || 'Student';

    // Generate appropriate message based on whether escalation occurred
    let consequenceDetails;
    if (escalationInfo && escalationInfo.escalatedType) {
      // Escalation message: "John's Friday Detention upgraded to Saturday Detention after 2 Friday Detentions in the year."
      const originalTypeValue = escalationInfo.originalType || rule.consequenceType || rule.escalateType;
      const originalTypeDoc = await consequenceTypesService.getByValue(originalTypeValue, schoolId);
      const originalTypeLabelPlural = originalTypeDoc?.label || originalTypeValue;
      const originalTypeLabel = eventStyleConfig.toSingular(originalTypeLabelPlural);

      const escalatedTypeDoc = await consequenceTypesService.getByValue(escalationInfo.escalatedType, schoolId);
      const escalatedTypeLabelPlural = escalatedTypeDoc?.label || escalationInfo.escalatedType;
      const escalatedTypeLabel = eventStyleConfig.toSingular(escalatedTypeLabelPlural);

      const pluralMonitorType = threshold === 1 ? monitorTypeLabel : (monitorTypeLabel.endsWith('s') ? monitorTypeLabel : `${monitorTypeLabel}s`);
      consequenceDetails = `${studentFirstName}'s ${originalTypeLabel} upgraded to ${escalatedTypeLabel} after ${threshold} ${pluralMonitorType} in the ${timeframe}.`;
    } else {
      // Normal threshold message: "John reached the threshold of 3 Demerits in the term."
      const pluralMonitorType = threshold === 1 ? monitorTypeLabel : (monitorTypeLabel.endsWith('s') ? monitorTypeLabel : `${monitorTypeLabel}s`);
      consequenceDetails = `${studentFirstName} reached the threshold of ${threshold} ${pluralMonitorType} in the ${timeframe}.`;
    }

    // Inherit timestamp from earliest triggering consequence for proper timeline ordering
    const inheritedTimestamp = this.getEarliestConsequenceTimestamp(triggeringDocs);

    return {
      schoolId: triggeringConsequence.schoolId,
      studentId: triggeringConsequence.studentId,
      studentName: triggeringConsequence.studentName,
      className: triggeringConsequence.className,
      classId: triggeringConsequence.classId,
      type: consequenceType,
      status: status,
      requiresAction: requiresAction,
      triggeredByRule: rule.id,
      ruleName: rule.title,
      ruleType: 'threshold',  // Add rule type for proper labeling
      triggeredBy: triggeringDocs.map(d => d.id),
      triggeringConsequences: triggeringDocs.map(d => d.id),
      consequenceType: consequenceType,
      consequenceCount: consequenceCount,
      consequenceDetails: consequenceDetails,
      severity: 'high',
      notes: `Auto-triggered by threshold rule: ${rule.title}`,
      ...snapshotStaffIdentity(triggeringConsequence),
      createdBy: 'system-threshold',
      autoGenerated: true,
      issuedAt: inheritedTimestamp,  // ✅ FIXED: Inherit from triggering consequences
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      undone: false,
      actioned: false,
      actionedBy: [],
      actionedAt: null
    };
  }

  /**
   * Mark consequences as consumed by threshold rule
   * @param {Array} docs - Document snapshots or doc-like objects
   * @param {string} ruleId - Rule ID
   * @returns {Promise<void>}
   */
  async markConsequencesAsConsumed(docs, ruleId) {
    const { db } = await import('../../../firebase');
    const { doc: firestoreDoc, updateDoc, Timestamp, arrayUnion } = await import('firebase/firestore');
    // Use static import instead of dynamic import

    for (const docItem of docs) {
      try {
        // Handle both real Firestore documents and plain objects
        let docRef;
        let dataObj;

        if (docItem.ref) {
          // It's a real Firestore document snapshot
          docRef = docItem.ref;
          dataObj = docItem.data();
        } else if (docItem.id) {
          // It's a plain object with an id - need to create a document reference
          // Determine the collection based on the data
          dataObj = typeof docItem.data === 'function' ? docItem.data() : docItem;

          // CRITICAL: Get schoolId for hierarchical path
          const schoolId = dataObj.schoolId;
          if (!schoolId) {
            disciplineLogger.critical('❌ Cannot mark as consumed: missing schoolId', {
              docId: docItem.id
            });
            continue;
          }

          // Use hierarchical collection path
          const collectionName = dataObj.mentionType ? 'mentions' : 'disciplineQueue';
          docRef = firestoreDoc(db, 'schools', schoolId, collectionName, docItem.id);
        } else {
          console.error('Invalid document format in markConsequencesAsConsumed:', docItem);
          continue;
        }

        const updateData = {
          usedByThresholdRule: ruleId,
          usedByThresholdAt: Timestamp.now(),
          actioned: true,
          actionedBy: arrayUnion(ruleId),
          actionedAt: Timestamp.now()
        };

        // Update Firestore FIRST
        await updateDoc(docRef, updateData);

        // ONLY update in-memory object if Firestore update succeeded
        // This ensures consistency - if Firestore fails, memory stays unchanged
        if (dataObj) {
          Object.assign(dataObj, {
            usedByThresholdRule: ruleId,
            usedByThresholdAt: updateData.usedByThresholdAt,
            actioned: true,
            actionedBy: [...(dataObj.actionedBy || []), ruleId],
            actionedAt: updateData.actionedAt
          });

          disciplineLogger.debug(`✅ Marked doc ${docItem.id} as consumed in BOTH memory and Firestore`, {
            docId: docItem.id,
            ruleId,
            collection: dataObj.mentionType ? 'mentions' : 'disciplineQueue'
          });
        }
      } catch (error) {
        if (isSchoolRulePeriodError(error) || isPhaseExecutionError(error)) throw error;
        // DO NOT update in-memory if Firestore update failed!
        console.error(`Failed to mark document ${docItem.id} as consumed in Firestore (memory unchanged):`, error);
        disciplineLogger.critical(`⚠️ SYNC WARNING: Doc ${docItem.id} NOT marked as consumed due to Firestore error`, {
          docId: docItem.id,
          error: error.message,
          willRetry: true
        });
        // Continue processing other documents even if one fails
      }
    }
  }
}
